import { useEffect, useRef, useState } from 'react';
import { SectionResponseDto } from '../../types/response';
import MetricCard, { MetricCardSkeleton } from '../metric-card/metric-card';
import MetricFunnel from '../metric-funnel/metric-funnel';
import type { RangeEditState } from '../metric-row/metric-row';

interface MetricSectionProps {
  section: SectionResponseDto;
  isEditMode: boolean;
  editedValues: Record<string, string>;
  editedRanges?: Record<string, RangeEditState>;
  onValueChange: (metricPath: string, value: string) => void;
  onRangeChange?: (metricPath: string, rangeState: RangeEditState) => void;
}

function sliceByCardPrefix<T>(sectionKey: string, cardKey: string, map: Record<string, T>): Record<string, T> {
  const prefix = `${sectionKey}.${cardKey}.`;
  const result: Record<string, T> = {};
  for (const key in map) {
    if (Object.prototype.hasOwnProperty.call(map, key) && key.startsWith(prefix)) {
      result[key] = map[key];
    }
  }
  return result;
}

const MetricSection = ({ section, isEditMode, editedValues, editedRanges = {}, onValueChange, onRangeChange }: MetricSectionProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(0);
  const cardCount = section.cards.length;
  const funnelCount = Math.max(0, cardCount - 1);
  const baseFunnelHeight = cardHeight > 0 ? cardHeight : 200;
  /** Jeder Funnel nach rechts 30 % kleiner (Faktor 0,7 pro Step). */
  const funnelHeightForStep = (stepIndex: number) => baseFunnelHeight * Math.pow(0.7, stepIndex);

  useEffect(() => {
    if (!cardRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCardHeight(entry.contentRect.height);
      }
    });
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-[#E4E7EC] px-6 pt-5 pb-6" style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.02)' }}>
      <h2 className="text-lg font-bold text-[#12282A] mb-5">{section.label}</h2>

      <div className="relative flex items-stretch justify-center">
        {funnelCount > 0 && (
          <div
            className="absolute inset-0 flex items-center justify-around pointer-events-none"
            style={{ zIndex: 0, paddingLeft: `${100 / cardCount / 2}%`, paddingRight: `${100 / cardCount / 2}%` }}>
            {Array.from({ length: funnelCount }).map((_, i) => (
              <MetricFunnel key={i} height={funnelHeightForStep(i)} />
            ))}
          </div>
        )}

        <div className="relative z-10 flex items-stretch justify-center w-full" style={{ gap: funnelCount > 0 ? '24px' : '16px' }}>
          {section.cards.map((card, index) => (
            <div key={card.cardKey} className="flex-1 min-w-[280px]" ref={index === 0 ? cardRef : undefined}>
              <MetricCard
                card={card}
                sectionKey={section.sectionKey}
                isEditMode={isEditMode}
                editedValues={sliceByCardPrefix(section.sectionKey, card.cardKey, editedValues)}
                editedRanges={sliceByCardPrefix(section.sectionKey, card.cardKey, editedRanges)}
                onValueChange={onValueChange}
                onRangeChange={onRangeChange}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MetricSection;

export const MetricSectionSkeleton = ({ cardCount = 4 }: { cardCount?: number }) => {
  const funnelCount = Math.max(0, cardCount - 1);

  return (
    <div
      className="bg-white rounded-2xl border border-[#E4E7EC] px-6 pt-5 pb-6 animate-pulse"
      style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.02)' }}>
      <div className="h-5 w-36 bg-[#E4E7EC] rounded-full mb-5" />

      <div className="relative flex items-stretch justify-center">
        <div className="relative z-10 flex items-stretch justify-center w-full" style={{ gap: funnelCount > 0 ? '24px' : '16px' }}>
          {Array.from({ length: cardCount }).map((_, i) => (
            <div key={i} className="flex-1 min-w-[280px]">
              <MetricCardSkeleton />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
